import React, { useState, useEffect, useRef } from 'react';
import api from '../../utils/api';
import styles from './ReportTree.module.css';

function ReportTree() {
    const [birds, setBirds] = useState([]);
    const [selectedBirdId, setSelectedBirdId] = useState('');
    const [treeData, setTreeData] = useState(null);
    const [aviaryData, setAviaryData] = useState(null);
    const [loading, setLoading] = useState(false);
    const certificateRef = useRef(null);

    const BASE_IMAGE_URL = process.env.REACT_APP_API_URL || 'https://reprobird.com.br/api';

    useEffect(() => {
        async function fetchBirds() {
            try {
                const token = localStorage.getItem('token');
                const response = await api.get('/bird/mybirds', {
                    headers: { Authorization: `Bearer ${JSON.parse(token)}` }
                });
                const birdList = response.data.birds || response.data || [];
                setBirds(birdList);
            } catch (err) {
                console.error('Erro ao buscar lista de pássaros:', err);
            }
        }
        fetchBirds();
    }, []);

    const handleSelectBird = async (e) => {
        const id = e.target.value;
        setSelectedBirdId(id);
        if (!id) {
            setTreeData(null);
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await api.get(`/report/genealogy/${id}`, {
                headers: { Authorization: `Bearer ${JSON.parse(token)}` }
            });

            setTreeData(response.data.birdTree);
            setAviaryData(response.data.aviary);
        } catch (err) {
            console.error('Erro ao carregar árvore:', err);
            alert('Não foi possível carregar os dados genealógicos.');
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const isFemale = (gender) => {
        if (!gender) return false;
        const g = String(gender).trim().toLowerCase();
        return g === 'female' || g === 'fêmea' || g === 'femea' || g === 'f';
    };

    // Helper para juntar e formatar as anilhas
    const formatBands = (node) => {
        if (!node) return '';
        const bands = [node.bandRight, node.bandLeft].filter(Boolean);
        return bands.length > 0 ? bands.join(' / ') : '';
    };

    // Renderiza a caixa com Nome e Anilha
    const renderNode = (node, defaultGender = 'male') => {
        const currentGender = node?.gender || defaultGender;
        const female = isFemale(currentGender);
        const cardClass = female ? styles.node_femea : styles.node_macho;
        const bandText = formatBands(node);

        return (
            <div className={`${styles.node_box} ${cardClass}`}>
                <span className={styles.node_name}>{node ? node.name : 'Nome'}</span>
                {bandText ? (
                    <span className={styles.node_band}>{bandText}</span>
                ) : (
                    <span className={styles.node_band}>S/ Anilha</span>
                )}
            </div>
        );
    };

    // --- EXTRAÇÃO DAS 4 GERAÇÕES ---
    const main = treeData;

    // GERAÇÃO 1: PAIS (2)
    const father = main?.father;
    const mother = main?.mother;

    // GERAÇÃO 2: AVÓS (4)
    const gFatherF = father?.father;
    const gMotherF = father?.mother;
    const gFatherM = mother?.father;
    const gMotherM = mother?.mother;

    // GERAÇÃO 3: BISAVÓS (8)
    const ggF1 = gFatherF?.father;
    const ggM1 = gFatherF?.mother;
    const ggF2 = gMotherF?.father;
    const ggM2 = gMotherF?.mother;
    const ggF3 = gFatherM?.father;
    const ggM3 = gFatherM?.mother;
    const ggF4 = gMotherM?.father;
    const ggM4 = gMotherM?.mother;

    // GERAÇÃO 4: TATARAVÓS (16)
    const ggg1 = ggF1?.father;  const ggg2 = ggF1?.mother;
    const ggg3 = ggM1?.father;  const ggg4 = ggM1?.mother;
    const ggg5 = ggF2?.father;  const ggg6 = ggF2?.mother;
    const ggg7 = ggM2?.father;  const ggg8 = ggM2?.mother;
    const ggg9 = ggF3?.father;  const ggg10 = ggF3?.mother;
    const ggg11 = ggM3?.father; const ggg12 = ggM3?.mother;
    const ggg13 = ggF4?.father; const ggg14 = ggF4?.mother;
    const ggg15 = ggM4?.father; const ggg16 = ggM4?.mother;

    const mainNodeSexClass = isFemale(main?.gender) ? styles.node_femea : styles.node_macho;
    const address = aviaryData?.address || {};
    const cityState = [address.city || aviaryData?.city, address.state || aviaryData?.state].filter(Boolean).join('/');

    return (
        <div className={styles.container}>
            {/* SELEÇÃO */}
            <div className={`${styles.filter_bar} no-print`}>
                <div className={styles.filter_group}>
                    <label>Selecione o Pássaro:</label>
                    <select value={selectedBirdId} onChange={handleSelectBird}>
                        <option value="">-- Selecione --</option>
                        {birds.map((b) => (
                            <option key={b._id} value={b._id}>
                                {b.name} ({b.bird_band_right || 'N/A'} : {b.bird_band_left || 'N/A'})
                            </option>
                        ))}
                    </select>
                </div>

                {treeData && (
                    <button className={styles.btn_print} onClick={handlePrint}>
                        🖨️ Imprimir / Salvar PDF
                    </button>
                )}
            </div>

            {loading && <p className={styles.loading_text}>Carregando árvore genealógica...</p>}

            {/* CERTIFICADO A4 PAISAGEM */}
            {treeData && (
                <div className={styles.certificate_wrapper}>
                    <div className={styles.certificate_card} ref={certificateRef}>
                        
                        <div className={styles.corner_top_left}></div>
                        <div className={styles.corner_bottom_right}></div>

                        {/* CABEÇALHO */}
                        <div className={styles.header}>
                            <div className={styles.aviary_info_box}>
                                <div className={styles.aviary_logo}>
                                    {aviaryData?.image ? (
                                        <img src={`${BASE_IMAGE_URL}/images/aviary/${aviaryData.image}`} alt="Logo do Criatório" />
                                    ) : (
                                        <div className={styles.shield_placeholder}>👑</div>
                                    )}
                                </div>
                                <div className={styles.aviary_details}>
                                    <h4>{aviaryData?.name || 'Criatório'}</h4>
                                    <p>CPF/CNPJ: {aviaryData?.document || '-'}</p>
                                    <p>E-mail: {aviaryData?.email || '-'}</p>
                                    <p>📞 {aviaryData?.phone || '-'}</p>
                                    <p>📍 {cityState || 'Cidade/UF'}</p>
                                </div>
                            </div>

                            <div className={styles.title_box}>
                                <h1>Certificado</h1>
                                <div className={styles.ribbon_banner}>
                                    <span>ÁRVORE GENEALÓGICA</span>
                                </div>
                            </div>
                        </div>

                        {/* DIAGRAMA DE 4 GERAÇÕES COMPLETO */}
                        <div className={styles.body_content}>
                            
                            {/* GERAÇÃO 0: AVE SELECIONADA */}
                            <div className={styles.col_target}>
                                <div className={`${styles.node_box} ${mainNodeSexClass} ${styles.target_node}`}>
                                    <span className={styles.node_name}>{main.name}</span>
                                    <span className={styles.node_band}>{formatBands(main) || 'S/ Anilha'}</span>
                                </div>

                                <div className={styles.bird_specs}>
                                    <p><strong>Raça:</strong> {main.race || '-'}</p>
                                    <p><strong>Cor:</strong> {main.color || '-'}</p>
                                    <p><strong>Anilha:</strong> {formatBands(main) || '-'}</p>
                                    <p><strong>Nasc:</strong> {main.birthday ? new Date(main.birthday).toLocaleDateString('pt-BR') : '-'}</p>
                                    <p><strong>Sexo:</strong> {isFemale(main.gender) ? 'Fêmea' : 'Macho'}</p>
                                    
                                    {/* Exibe o parágrafo de Info apenas se main.comments existir e não estiver vazio */}
                                    {main.comments && main.comments.trim() !== '' && (
                                        <p><strong>Info:</strong> {main.comments}</p>
                                    )}
                                </div>
                            </div>

                            <div className={styles.line_connector_single}></div>

                            {/* GERAÇÃO 1: PAIS (2) */}
                            <div className={styles.col_parents}>
                                {renderNode(father, 'male')}
                                {renderNode(mother, 'female')}
                            </div>

                            <div className={styles.line_connector_double}>
                                <div className={styles.connector_branch}></div>
                                <div className={styles.connector_branch}></div>
                            </div>

                            {/* GERAÇÃO 2: AVÓS (4) */}
                            <div className={styles.col_grandparents}>
                                {renderNode(gFatherF, 'male')}
                                {renderNode(gMotherF, 'female')}
                                {renderNode(gFatherM, 'male')}
                                {renderNode(gMotherM, 'female')}
                            </div>

                            <div className={styles.line_connector_quad}>
                                <div className={styles.connector_branch_sm}></div>
                                <div className={styles.connector_branch_sm}></div>
                                <div className={styles.connector_branch_sm}></div>
                                <div className={styles.connector_branch_sm}></div>
                            </div>

                            {/* GERAÇÃO 3: BISAVÓS (8) */}
                            <div className={styles.col_great_grandparents}>
                                {renderNode(ggF1, 'male')}
                                {renderNode(ggM1, 'female')}
                                {renderNode(ggF2, 'male')}
                                {renderNode(ggM2, 'female')}
                                {renderNode(ggF3, 'male')}
                                {renderNode(ggM3, 'female')}
                                {renderNode(ggF4, 'male')}
                                {renderNode(ggM4, 'female')}
                            </div>

                            <div className={styles.line_connector_octa}>
                                <div className={styles.connector_branch_xs}></div>
                                <div className={styles.connector_branch_xs}></div>
                                <div className={styles.connector_branch_xs}></div>
                                <div className={styles.connector_branch_xs}></div>
                                <div className={styles.connector_branch_xs}></div>
                                <div className={styles.connector_branch_xs}></div>
                                <div className={styles.connector_branch_xs}></div>
                                <div className={styles.connector_branch_xs}></div>
                            </div>

                            {/* GERAÇÃO 4: TATARAVÓS (16) */}
                            <div className={styles.col_tatara_grandparents}>
                                {renderNode(ggg1, 'male')}
                                {renderNode(ggg2, 'female')}
                                {renderNode(ggg3, 'male')}
                                {renderNode(ggg4, 'female')}
                                {renderNode(ggg5, 'male')}
                                {renderNode(ggg6, 'female')}
                                {renderNode(ggg7, 'male')}
                                {renderNode(ggg8, 'female')}
                                {renderNode(ggg9, 'male')}
                                {renderNode(ggg10, 'female')}
                                {renderNode(ggg11, 'male')}
                                {renderNode(ggg12, 'female')}
                                {renderNode(ggg13, 'male')}
                                {renderNode(ggg14, 'female')}
                                {renderNode(ggg15, 'male')}
                                {renderNode(ggg16, 'female')}
                            </div>

                        </div>

                        {/* RODAPÉ */}
                        <div className={styles.footer}>
                            <div className={styles.footer_brand}>
                                <span><strong>www.reprobird.com.br</strong></span>
                            </div>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
}

export default ReportTree;